import {
  AlertTriangle,
  ArrowLeft,
  Info,
  Loader2,
  MessagesSquare,
  Pencil,
  RefreshCw,
  Share2,
  Trash2,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useConfirm, useToast } from "@/components/shared";
import { getApiErrorMessage } from "@/lib/api";
import { microAppService } from "@/lib/services";
import type { MicroApp } from "@/types";
import { injectHydrationRuntime } from "./microApps/hydrationRuntime";
import { MicroAppAccessModal } from "./microApps/MicroAppAccessModal";
import { MicroAppAuthControl } from "./microApps/MicroAppAuthControl";
import { MicroAppInfoModal } from "./microApps/MicroAppInfoModal";
import { useDashboardHydration } from "./microApps/useDashboardHydration";

function formatRelative(date: Date): string {
  const seconds = Math.round((Date.now() - date.getTime()) / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return date.toLocaleDateString();
}

function LiveBadge({
  lastUpdated,
  refreshing,
  errorCount,
  onRefresh,
}: Readonly<{ lastUpdated: Date | null; refreshing: boolean; errorCount: number; onRefresh: () => void }>) {
  const hasError = errorCount > 0;
  return (
    <div className="flex items-center gap-2 text-xs text-content-tertiary">
      {hasError ? (
        <span
          className="inline-flex items-center gap-1.5 text-status-warning"
          title={`${errorCount} value${errorCount === 1 ? "" : "s"} couldn't refresh — showing last known data`}
        >
          <AlertTriangle className="h-3.5 w-3.5" aria-hidden />
          {lastUpdated ? `Stale · ${formatRelative(lastUpdated)}` : "Couldn't refresh"}
        </span>
      ) : (
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-full bg-status-success" aria-hidden />
          Live{lastUpdated ? ` · updated ${formatRelative(lastUpdated)}` : ""}
        </span>
      )}
      <button
        type="button"
        onClick={onRefresh}
        disabled={refreshing}
        className="p-1 rounded hover:bg-interactive-hover transition disabled:opacity-50"
        aria-label="Refresh data"
      >
        <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} />
      </button>
    </div>
  );
}

function EditableTitle({
  name,
  canEdit,
  onRename,
}: Readonly<{ name: string; canEdit: boolean; onRename: (next: string) => Promise<void> }>) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(name);
  const committingRef = useRef(false);

  const start = () => {
    if (!canEdit) return;
    setDraft(name);
    setEditing(true);
  };

  const commit = async () => {
    if (committingRef.current) return;
    committingRef.current = true;
    const trimmed = draft.trim();
    setEditing(false);
    if (trimmed && trimmed !== name) {
      await onRename(trimmed);
    }
    committingRef.current = false;
  };

  const cancel = () => {
    committingRef.current = true; // suppress the onBlur that follows Escape
    setEditing(false);
    setTimeout(() => {
      committingRef.current = false;
    }, 0);
  };

  if (editing) {
    return (
      <input
        type="text"
        value={draft}
        maxLength={255}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") void commit();
          else if (e.key === "Escape") cancel();
        }}
        aria-label="Micro app name"
        className="min-w-0 flex-1 max-w-md bg-surface-secondary border border-brand-primary/50 rounded-lg px-2 py-1 text-base font-semibold text-content-primary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50"
      />
    );
  }

  return (
    <div className="flex items-center gap-1.5 min-w-0">
      <h1
        className={`text-base font-semibold text-content-primary truncate ${canEdit ? "cursor-text" : ""}`}
        onDoubleClick={start}
        title={canEdit ? "Double-click to rename" : undefined}
      >
        {name}
      </h1>
      {canEdit && (
        <button
          type="button"
          onClick={start}
          aria-label="Rename micro app"
          className="p-1 rounded hover:bg-interactive-hover text-content-tertiary transition opacity-0 group-hover:opacity-100 focus:opacity-100"
        >
          <Pencil className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}

function ViewerHeader({
  app,
  onBack,
  onShare,
  onInfo,
  onRename,
  onDelete,
  onEditInChat,
  live,
}: Readonly<{
  app: MicroApp;
  onBack: () => void;
  onShare: () => void;
  onInfo: () => void;
  onRename: (next: string) => Promise<void>;
  onDelete: () => void;
  onEditInChat: (() => void) | null;
  live?: React.ReactNode;
}>) {
  return (
    <div className="group shrink-0 flex items-center justify-between px-4 py-3 border-b border-border-primary">
      <div className="flex items-center gap-3 min-w-0">
        <button
          type="button"
          onClick={onBack}
          className="p-1.5 rounded-lg hover:bg-interactive-hover text-content-tertiary transition"
          aria-label="Back to micro apps"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <EditableTitle name={app.name} canEdit={app.canEdit} onRename={onRename} />
        {live}
      </div>
      <div className="flex items-center gap-2">
        {onEditInChat && (
          <button
            type="button"
            onClick={onEditInChat}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-surface-secondary hover:bg-interactive-active text-content-primary text-sm font-medium border border-border-secondary transition"
          >
            <MessagesSquare className="h-4 w-4" /> Edit in chat
          </button>
        )}
        <button
          type="button"
          onClick={onInfo}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-surface-secondary hover:bg-interactive-active text-content-primary text-sm font-medium border border-border-secondary transition"
        >
          <Info className="h-4 w-4" /> About
        </button>
        <button
          type="button"
          onClick={onShare}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-surface-secondary hover:bg-interactive-active text-content-primary text-sm font-medium border border-border-secondary transition"
        >
          <Share2 className="h-4 w-4" /> Share
        </button>
        {app.canManage && (
          <button
            type="button"
            onClick={onDelete}
            aria-label="Delete micro app"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-surface-secondary hover:bg-red-500/10 hover:text-red-400 hover:border-red-500/30 text-content-tertiary text-sm font-medium border border-border-secondary transition"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
}

export function MicroAppViewer() {
  const { uuid } = useParams<{ uuid: string }>();
  const navigate = useNavigate();
  const { confirm } = useConfirm();
  const { showToast } = useToast();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [app, setApp] = useState<MicroApp | null>(null);
  const [html, setHtml] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);

  // Inject the trusted hydration runtime + CSP once per HTML load.
  const srcDoc = useMemo(() => (html ? injectHydrationRuntime(html) : ""), [html]);

  const { isLive, lastUpdated, refreshing, errorCount, refresh } = useDashboardHydration({ uuid, html, iframeRef });

  useEffect(() => {
    if (!uuid) return;
    let active = true;
    void (async () => {
      try {
        const [meta, content] = await Promise.all([microAppService.get(uuid), microAppService.getContent(uuid)]);
        if (active) {
          setApp(meta.data);
          setHtml(content);
        }
      } catch {
        if (active) setError("This micro app could not be loaded.");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [uuid]);

  const handleRename = async (next: string) => {
    if (!uuid) return;
    try {
      const response = await microAppService.update(uuid, { name: next });
      setApp(response.data);
      showToast("Name updated", "success");
    } catch (err) {
      showToast(getApiErrorMessage(err) || "Failed to rename micro app", "error");
    }
  };

  const handleDelete = async () => {
    if (!uuid || !app) return;
    const confirmed = await confirm({
      title: "Delete micro app",
      message: `Delete “${app.name}”? People it was shared with will lose access. This action cannot be undone.`,
      confirmText: "Delete",
      variant: "danger",
    });
    if (!confirmed) return;
    try {
      await microAppService.delete(uuid);
      showToast("Micro app deleted", "success");
      navigate("/apps/view");
    } catch (err) {
      showToast(getApiErrorMessage(err) || "Failed to delete micro app", "error");
    }
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-brand-primary animate-spin" />
      </div>
    );
  }

  if (error || !app) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-center p-6">
        <p className="text-content-secondary">{error ?? "Micro app not found."}</p>
        <button
          type="button"
          onClick={() => navigate("/apps/view")}
          className="mt-4 text-sm text-brand-primary hover:underline"
        >
          Back to Micro Apps
        </button>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col min-h-0">
      <ViewerHeader
        app={app}
        onBack={() => navigate("/apps/view")}
        onShare={() => setShareOpen(true)}
        onInfo={() => setInfoOpen(true)}
        onRename={handleRename}
        onDelete={handleDelete}
        onEditInChat={
          app.canEdit && app.sourceSessionUuid
            ? () =>
                navigate(`/chat/${app.sourceSessionUuid}`, {
                  state: { updateAppUuid: app.uuid, updateAppName: app.name },
                })
            : null
        }
        live={
          isLive ? (
            <LiveBadge lastUpdated={lastUpdated} refreshing={refreshing} errorCount={errorCount} onRefresh={refresh} />
          ) : null
        }
      />
      <MicroAppAuthControl appUuid={app.uuid} />
      <iframe
        ref={iframeRef}
        srcDoc={srcDoc}
        sandbox="allow-scripts"
        title={`Micro App: ${app.name}`}
        className="w-full flex-1 min-h-0 border-0"
        style={{ background: "white" }}
      />
      <MicroAppAccessModal
        appUuid={app.uuid}
        appName={app.name}
        isOpen={shareOpen}
        onClose={() => setShareOpen(false)}
      />
      <MicroAppInfoModal appUuid={app.uuid} isOpen={infoOpen} onClose={() => setInfoOpen(false)} />
    </div>
  );
}
